import React, { useState, useRef } from "react";
import axios from "axios";

const PER_PAGE = 50;

const SongSearchBox = ({ onSelect, onClose }) => {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState(null); // "title" | "artist" | null
  const [results, setResults] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const requestIdRef = useRef(0);
  const cancelSourceRef = useRef(null);

  const toggleFilter = (value) => {
    setFilter((current) => (current === value ? null : value));
  };

  const fetchResults = (pageToFetch) => {
    const trimmed = query.trim();
    if (!trimmed) return;

    if (cancelSourceRef.current) cancelSourceRef.current.cancel();
    const cancelSource = axios.CancelToken.source();
    cancelSourceRef.current = cancelSource;

    const requestId = ++requestIdRef.current;
    setLoading(true);

    axios
      .get("/api/v1/games/search_songs", {
        params: {
          query: trimmed,
          title: filter === "title" ? true : undefined,
          artist: filter === "artist" ? true : undefined,
          page: pageToFetch,
          per_page: PER_PAGE,
        },
        cancelToken: cancelSource.token,
      })
      .then((response) => {
        if (requestId !== requestIdRef.current) return;
        const data = response.data || {};
        setResults(data.songs || []);
        setPage(data.page || pageToFetch);
        setTotalPages(data.total_pages || 0);
        setTotal(data.total || 0);
        setSearched(true);
        setLoading(false);
      })
      .catch((error) => {
        if (axios.isCancel(error)) return;
        if (requestId !== requestIdRef.current) return;
        setLoading(false);
      });
  };

  const handleSearchClick = () => fetchResults(1);

  const handlePageChange = (nextPage) => {
    if (nextPage < 1 || nextPage > totalPages) return;
    fetchResults(nextPage);
  };

  const handleSelect = (song) => {
    setResults([]);
    setSearched(false);
    setQuery("");
    if (onSelect) onSelect(song);
  };

  return (
    <div className="song-search-box" style={{ position: "relative" }}>
      <div className="input-group">
        <input
          type="text"
          className="form-control"
          placeholder="Search by title or artist"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className="input-group-append">
          <button
            type="button"
            className={`btn btn-outline-secondary${filter === "title" ? " active" : ""}`}
            onClick={() => toggleFilter("title")}
          >
            Title
          </button>
          <button
            type="button"
            className={`btn btn-outline-secondary${filter === "artist" ? " active" : ""}`}
            onClick={() => toggleFilter("artist")}
          >
            Artist
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={handleSearchClick}
          >
            <i className="fa fa-search" />
          </button>
          {onClose && (
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={onClose}
            >
              <i className="fa fa-times" />
            </button>
          )}
        </div>
      </div>

      {(loading || searched) && (
        <div
          className="dropdown-menu show"
          style={{ width: "100%", maxHeight: 300, overflowY: "auto" }}
        >
          {loading && (
            <span className="dropdown-item-text">Searching...</span>
          )}
          {!loading && results.length === 0 && (
            <span className="dropdown-item-text">No songs found</span>
          )}
          {!loading &&
            results.map((song) => (
              <button
                key={song.id}
                type="button"
                className="dropdown-item"
                onClick={() => handleSelect(song)}
              >
                <b>{song.title}</b> by <b>{song.artist}</b> ({song.year})
              </button>
            ))}
          {!loading && totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center px-2 py-1">
              <button
                type="button"
                className="btn btn-sm btn-link"
                disabled={page <= 1}
                onClick={() => handlePageChange(page - 1)}
              >
                Prev
              </button>
              <span>
                Page {page} of {totalPages} ({total} results)
              </span>
              <button
                type="button"
                className="btn btn-sm btn-link"
                disabled={page >= totalPages}
                onClick={() => handlePageChange(page + 1)}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SongSearchBox;
